<template>
    <div v-click-outside="endNotifications">
      <div class="notification-container">
        <svg @click="showNotification = !showNotification" style="cursor: pointer;" width="30" height="30" viewBox="0 0 24 24" role="presentation"><path d="M6.485 17.669a2 2 0 002.829 0l-2.829-2.83a2 2 0 000 2.83zm4.897-12.191l-.725.725c-.782.782-2.21 1.813-3.206 2.311l-3.017 1.509c-.495.248-.584.774-.187 1.171l8.556 8.556c.398.396.922.313 1.171-.188l1.51-3.016c.494-.988 1.526-2.42 2.311-3.206l.725-.726a5.048 5.048 0 00.64-6.356 1.01 1.01 0 10-1.354-1.494c-.023.025-.046.049-.066.075a5.043 5.043 0 00-2.788-.84 5.036 5.036 0 00-3.57 1.478z" fill="currentColor" fill-rule="evenodd"></path></svg>
        <!-- Circle span to display notification count -->
        <span class="notification-count">
          {{notificationCount}}
        </span>
      </div>
      <div class="notification-box" v-if="showNotification" ref="notificationContainer">
        <div class="notification-navbar">
          <span class="pl-3">Notifications</span>
          <!-- <img
          src="~@/assets/images/icons/more-icon.svg"
          class="file-card-action--btn"
          alt=""
        /> -->
        </div>
        <div class="box">
          <div class="center" v-if="loading">
            <loading-circle/>
          </div>
          <div v-else>
        <div v-for="(notification, index) in notificationsToShow" :key="notification.id" class="notification-item">
          <div class="notification-content pointer" :class="{'highlighted': shouldHighlight(notification)}" @click="markNotificationAsRead(notification)">
            <!-- <router-link :to="notification.link"> -->
              <div class="notification-message" @click.stop="clickNotification(notification)">
                {{ notification.message }}
              </div>
            <!-- </router-link> -->
            <div class="notification-time">
              <span :class="{'unread-dot': shouldHighlight(notification)}"></span>
              {{ notification.time }}
            </div>
          </div>
          <div class="notification-separator"></div>
          <Loader v-if="index === notificationsToShow.length - 1 && !reachedEndOfNotifications && loading" />
        </div>
        </div>
        </div>
      </div>
    </div>
  </template>

<script>
import { LiveNotifications, ChangeReadStatus } from '../../api/apis/notifications'
import { getDocumentById } from '@/api'
import { getTokenStorage, logout, projectExchangeToken, factoryExchangeToken } from '@/api/session'
import loadingCircle from '../../components/common/loadingCircle.vue'
import { Duration } from '../../utils/date'
import { mapGetters } from 'vuex'
import config from '@/config'
import jwtDecode from 'jwt-decode'
import { alert } from '@/plugins/notification'
export default {
  components: { loadingCircle },
  props: {
    notifications: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      loading: true,
      showNotification: false,
      notificationCount: '0',
      page: 0, // current page number
      notificationsPerPage: 50, // number of notifications per page
      allNotifications: [],
      highlightedNotificationId: null,
      reachedEndOfNotifications: false
    }
  },
  computed: {
    ...mapGetters([
      'collaborator',
      'isExternalCollaborator',
      'user', 'isOnFactoryLevel'
    ]),
    notificationsToShow () {
      const startIndex = this.page * this.notificationsPerPage
      const endIndex = startIndex + this.notificationsPerPage
      return this.allNotifications.slice(startIndex, endIndex)
    }
  },
  mounted () {
    this.subscribeToNotifications()
  },
  watch: {
    isExternalCollaborator () {
      this.subscribeToNotifications()
    }
  },
  methods: {
    endNotifications () {
      this.showNotification = false
    },
    appendQueryParamToLink (link, key, value) {
      if (!link || !key) return link
      const separator = link.includes('?') ? '&' : '?'
      return `${link}${separator}${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    },
    shouldForceRefreshRoute (notification) {
      const action = String(notification?.action || '').toUpperCase()
      return [
        'DOCUMENT_CREATE',
        'DOCUMENT_CHECKIN',
        'DOCUMENT_CHECKOUT',
        'DOCUMENT_VIEW',
        'DOCUMENT_DOWNLOAD',
        'DOCUMENT_SHARE',
        'TASK_ASSIGN',
        'TASK_UPDATE',
        'TASK_PROGRESS_UPDATE',
        'BOM_SHARE'
      ].includes(action)
    },
    getNavigableLink (notification) {
      if (!notification?.link) return ''
      if (!this.shouldForceRefreshRoute(notification)) return notification.link
      return this.appendQueryParamToLink(notification.link, '_n', `${notification.id}-${Date.now()}`)
    },
    parseDocumentIdsFromLink (link) {
      if (!link) return { parentDocId: null, selectedDocId: null }
      try {
        const parsed = new URL(link, window.location.origin)
        const pathParts = parsed.pathname.split('/').filter(Boolean)
        const parentDocId = pathParts[0] === 'document-view' ? this.sanitizeId(pathParts[1]) : null
        const selectedDocId = this.sanitizeId(parsed.searchParams.get('selectedDocId') || parsed.searchParams.get('selecteddocid'))
        return { parentDocId, selectedDocId }
      } catch (error) {
        return { parentDocId: null, selectedDocId: null }
      }
    },
    async resolveProjectIdForDocumentNotification (notification) {
      const { parentDocId, selectedDocId } = this.parseDocumentIdsFromLink(notification?.link)
      const candidateIds = [parentDocId, selectedDocId].filter(Boolean)
      for (const documentId of candidateIds) {
        try {
          const response = await getDocumentById(documentId, 'tenant')
          const doc = response?.core_documents?.[0]
          const projectId = this.sanitizeId(doc?.project_id)
          if (projectId) return projectId
        } catch (error) {}
      }
      return null
    },
    async clickNotification (notification) {
      this.markNotificationAsRead(notification)
      this.showNotification = false
      if (!notification?.link) return
      let effectiveProjectId = this.sanitizeId(notification?.projectId)
      if (!effectiveProjectId && String(notification?.action || '').toUpperCase().startsWith('DOCUMENT_')) {
        effectiveProjectId = await this.resolveProjectIdForDocumentNotification(notification)
      }
      const resolvedNotification = {
        ...notification,
        projectId: effectiveProjectId || notification?.projectId
      }
      const targetLink = this.getNavigableLink(notification)
      const storage = getTokenStorage()
      const prevProjectId = storage.getItem('projectId')
      const shouldHandleFactoryContext = this.shouldHandleFactoryContext(resolvedNotification)
      const shouldHandleProjectContext = this.shouldHandleProjectContext(resolvedNotification)
      const shouldHandleMasterContext = this.shouldHandleMasterContext(resolvedNotification)
      if (this.isOnFactoryLevel && (shouldHandleProjectContext || shouldHandleMasterContext)) {
        this.$store.commit('resetFactory')
        storage.removeItem('dtxFactoryToken')
        storage.removeItem('factoryId')
      }
      if (shouldHandleFactoryContext && resolvedNotification.factoryId) {
        factoryExchangeToken(resolvedNotification.factoryId, () => {
          this.$store.dispatch('getCurrentFactoryData')
            .catch(() => {})
            .finally(() => {
              this.$router.push(targetLink).catch(() => {})
            })
        })
        return
      }
      if (shouldHandleMasterContext) {
        const isAlreadyOnMasterLevel = !prevProjectId || String(prevProjectId) === '0'
        storage.setItem(config.localstorageKeys.LAST_OPENED_PROJECT, 0)
        storage.removeItem(config.localstorageKeys.PROJECT)
        this.$store.commit('setCurrentProject', { id: 0 })
        this.$store.commit('setUserProjectLevelRole', null)
        this.$store.commit('setProjectIdForCollaborator', null)
        if (!isAlreadyOnMasterLevel) {
          // Avoid setup() here because layout watcher on project-planner can force redirect back to /project-planner.
          // We only need to clear project context for tenant/master notifications.
        }
        this.$router.push(targetLink).catch(() => {})
        return
      }
      if (!shouldHandleProjectContext) {
        this.$router.push(targetLink).catch(() => {})
        return
      }
      const newProjectId = resolvedNotification.projectId
      storage.setItem('projectId', newProjectId)
      projectExchangeToken(() => {
        const hasProjectChanged = String(prevProjectId || '') !== String(newProjectId || '')
        const syncContextPromise = hasProjectChanged
          ? new Promise((resolve) => {
            this.$store.dispatch('setup', () => resolve())
          })
          : this.$store.dispatch('getCurrentProject')
        Promise.resolve(syncContextPromise)
          .catch(() => {})
          .finally(() => {
            this.$router.push(targetLink).catch(() => {})
          })
      }, newProjectId)
    },
    notificationss () {
      this.showNotification = !this.showNotification
    },
    subscribeToNotifications () {
      LiveNotifications(this.handleNotifications, this.page, this.notificationsPerPage, this.user)
    },
    shouldHandleFactoryContext (notification) {
      return notification?.isFactory === true
    },
    shouldHandleProjectContext (notification) {
      if (!notification?.projectId || notification?.projectId === '0' || notification?.projectId === 0) {
        return false
      }
      return !this.shouldHandleFactoryContext(notification)
    },
    shouldHandleMasterContext (notification) {
      const projectId = notification?.projectId
      return !this.shouldHandleFactoryContext(notification) && (!projectId || projectId === '0' || projectId === 0)
    },
    parseMetadata (metadata) {
      if (!metadata) return {}
      if (typeof metadata === 'object') return metadata
      try {
        return JSON.parse(metadata)
      } catch (error) {
        return {}
      }
    },
    getWorkflowType (type) {
      const normalizedType = String(type || '').toUpperCase()
      return ['FULL', 'PARTIAL', 'NORMAL'].includes(normalizedType) ? normalizedType : null
    },
    getDocumentLink (parentDocId, documentId) {
      if (!documentId) return '/document-view'
      if (!parentDocId) return `/document-view?selectedDocId=${documentId}`
      return `/document-view/${parentDocId}?selectedDocId=${documentId}`
    },
    sanitizeId (value) {
      if (!value) return null
      const parsedValue = String(value).trim()
      if (!parsedValue || ['null', 'undefined', 'NaN'].includes(parsedValue)) return null
      return parsedValue
    },
    pickFirstValidId (...values) {
      for (const value of values) {
        const id = this.sanitizeId(value)
        if (id) return id
      }
      return null
    },
    getTaskPlannerLink (taskId) {
      return taskId ? `/project-planner?view=board&selectedTaskId=${taskId}` : '/project-planner?view=board'
    },
    getMaterialLink (materialId) {
      return materialId ? `/materialmaster/1?selectedMaterialId=${materialId}` : '/materialmaster/1'
    },
    getFormLink (templateId, formId) {
      if (templateId && formId) return `/forms/template/${templateId}/form/${formId}/view`
      return '/forms'
    },
    getBomLink (projectId, productId, bomId, bomVersionId) {
      const versionQuery = bomVersionId ? `?bomVersionId=${bomVersionId}` : ''
      if (projectId) {
        if (bomId) return `/bom/project/copy-bom/${bomId}${versionQuery}`
        return '/bom/project'
      }
      if (productId && bomId) return `/bom/product/${productId}/copy-bom/${bomId}${versionQuery}`
      if (productId) return `/bom/product/${productId}`
      return '/bom/product'
    },
    shouldHighlight (notification) {
      return !notification.readStatus
    },
    markNotificationAsRead (notification) {
      if (!notification.readStatus) {
        notification.readStatus = true // Mark the clicked notification as read
        ChangeReadStatus(notification.id)
      }
    },
    handleNotifications (data, error) {
      const storage = getTokenStorage()
      this.loading = true
      if (error) {
        this.allNotifications = [{ id: 0, message: 'Something went wrong! Refresh again' }]
        this.loading = false
        // alert('Something went wrong, Try refreshing again.')
        // this is not required
      } else if (!data.length) {
        this.allNotifications = [{ id: 0, message: 'There are no notifications right now!' }]
        this.loading = false
      } else {
        this.notificationCount = data.filter(notification => !notification.read_status).length + '+'
        const transformedNotifications = []
        for (const notification of data) {
          const { id, sent_on: sentOn, project_id: projectId, form_id: formIdFromNotification, task_id: taskIdFromNotification, material_id: materialIdFromNotification, read_status: readStatus, core_material_master: coreMaterialMaster, core_bom: coreBom, sent_by_user: sentByUser, action, product_code: productCode, metadata: metaData, core_project: project, workflow_instance_id: workflowInstanceId, feature } = notification
          const bomName = coreBom ? coreBom?.name : ''
          const bomId = this.sanitizeId(coreBom?.id)
          const bomVersionId = this.sanitizeId(coreBom?.bom_versions?.[0]?.id)
          const userName = sentByUser?.first_name || ''
          const productName = productCode?.product_code || ''
          const productId = this.sanitizeId(coreBom?.product_code || productCode?.id)
          const materialName = coreMaterialMaster?.material_name || ''
          const materialId = this.sanitizeId(coreMaterialMaster?.id || materialIdFromNotification)
          // const taskId = taskData?.id || ''
          const time = Duration(sentOn)
          const metadata = this.parseMetadata(metaData)
          const previousStepName = metadata?.previous_step_name
          const currentStepName = metadata?.current_step_name
          const taskName = metadata?.task_name || ''
          const templateName = metadata?.template_name || ''
          const documentName = metadata ? metadata?.doc_name : ''
          const selectedDocumentId = this.pickFirstValidId(
            metadata?.doc_id,
            metadata?.document_id,
            metadata?.selected_doc_id,
            metadata?.selectedDocId,
            notification.document_id
          )
          const parentDocId = this.pickFirstValidId(
            metadata?.parent_doc_id,
            metadata?.parent_document_id,
            metadata?.parent_id,
            metadata?.parentDocId,
            metadata?.folder_id,
            metadata?.folderId
          )
          const workflowType = metadata ? metadata?.workflow_type : ''
          const workFlowName = metadata ? metadata?.workflow_name : ''
          const taskId = this.pickFirstValidId(taskIdFromNotification, metadata?.task_id, metadata?.taskId)
          const formId = this.pickFirstValidId(formIdFromNotification, metadata?.form_id, metadata?.formId)
          // const documentId = metadata ? metadata?.doc_id : ''
          // console.log(documentId, 'doc')
          // const formId = metadata?.form_id
          // const taskId = metadata?.task_id
          const templateId = this.pickFirstValidId(metadata?.template_id, metadata?.templateId, metadata?.form_template_id, metadata?.formTemplateId)
          const projectID = this.pickFirstValidId(projectId, metadata?.project_id, metadata?.projectId)
          const isFactory = Boolean(notification?.is_factory ?? project?.is_factory ?? metadata?.is_factory)
          const factoryId = isFactory ? (metadata?.factory_id || projectId || metadata?.project_id || null) : null
          const tempalateVersionId = metadata?.template_version_id || null
          const materailProcessName = metadata?.material_name
          const materailProcessQuantity = metadata?.quantity
          const materialFormId = metadata ? metadata?.formId : null
          const workflowRouteType = this.getWorkflowType(workflowType)
          const metadataRoute = metadata?.redirect_url || metadata?.redirectUrl || metadata?.route || metadata?.link || metadata?.redirect_path
          let notificationMessage = ''
          let notificationLink = ''
          switch (action) {
          case 'USER_GROUP_INVITE':
            notificationMessage = `You are added to a user group named ${metadata?.group_name}`
            notificationLink = metadata?.group_id ? `/settings/user-groups/detail/${metadata?.group_id}` : '/settings/user-groups'
            break
          case 'WORKFLOW_PROGRESSION':
            notificationMessage = `${userName} has transitioned ${workFlowName} from ${previousStepName} to ${currentStepName}`
            if (workflowRouteType) {
              notificationLink = `/workflows/${workflowRouteType}/?version=${tempalateVersionId}&wfinstance=${workflowInstanceId}`
            } else {
              notificationLink = '/workflow-dashboard'
            }
            break
          case 'DOCUMENT_VIEW':
            notificationMessage = `Document ${documentName} is viewed by the user ${userName}`
            notificationLink = this.getDocumentLink(parentDocId, selectedDocumentId)
            break
          case 'DOCUMENT_DOWNLOAD':
            notificationMessage = `Document ${documentName} is downloaded by the user ${userName}`
            notificationLink = this.getDocumentLink(parentDocId, selectedDocumentId)
            break
          case 'BOM_CREATE':
            notificationMessage = `A new BOM ${bomName} has been created by ${userName}.`
            notificationLink = this.getBomLink(projectID, productId, bomId, bomVersionId)
            break

          case 'BOM_CHECKIN':
            notificationMessage = `BOM ${bomName} has been checked in by ${userName}.`
            notificationLink = this.getBomLink(projectID, productId, bomId, bomVersionId)
            break

          case 'BOM_CHECKOUT':
            notificationMessage = `BOM ${bomName} has been checked out by ${userName}.`
            notificationLink = this.getBomLink(projectID, productId, bomId, bomVersionId)
            break

          case 'BOM_OBSOLETE':
            notificationMessage = `The BOM ${bomName} has been made obselete by ${userName}.`
            notificationLink = this.getBomLink(projectID, productId, bomId, bomVersionId)
            break

          case 'DOCUMENT_CREATE':
            notificationMessage = `The Document ${documentName} has been created by ${userName}.`
            notificationLink = this.getDocumentLink(parentDocId, selectedDocumentId)
            break

          case 'DOCUMENT_CHECKIN':
            notificationMessage = `The Document ${documentName} has been Checked in by ${userName}.`
            notificationLink = this.getDocumentLink(parentDocId, selectedDocumentId)
            break

          case 'DOCUMENT_CHECKOUT':
            notificationMessage = `The Document ${documentName} has been Checked out by ${userName}.`
            notificationLink = this.getDocumentLink(parentDocId, selectedDocumentId)
            break

          case 'PRODUCT_CODE_CREATE':
            notificationMessage = `A new product ${productName} has been created by ${userName}.`
            notificationLink = productId ? `/bom/product/${productId}` : '/bom/product'
            break

          case 'MATERIAL_CREATE':
            notificationMessage = `A new Material ${materialName} has been created by ${userName}.`
            notificationLink = this.getMaterialLink(materialId)
            break

          case 'MATERIAL_OBSOLETE':
            notificationMessage = `The Material ${materialName} has been made obselete by ${userName}.`
            notificationLink = this.getMaterialLink(materialId)
            break

          case 'FORM_CREATE':
            notificationMessage = `A new Form has been created by ${userName} in template ${templateName}.`
            notificationLink = this.getFormLink(templateId, formId)
            break

          case 'FORM_UPDATE':
            notificationMessage = `The Form from template ${templateName} has been updated by ${userName}.`
            notificationLink = this.getFormLink(templateId, formId)
            break

          case 'TASK_PROGRESS_UPDATE':
            notificationMessage = `Task ${taskName} progress has been updated`
            notificationLink = this.getTaskPlannerLink(taskId)
            break

          case 'TASK_ASSIGN':
            notificationMessage = `The Task ${taskName} has been assigned to you by ${userName}.`
            notificationLink = this.getTaskPlannerLink(taskId)
            break

          case 'BOM_SHARE':
            notificationMessage = `The Bom has been shared to you by ${userName}.`
            notificationLink = this.getTaskPlannerLink(taskId)
            break

          case 'DOCUMENT_SHARE':
            notificationMessage = `The Document ${documentName} has been shared to you by ${userName}.`
            notificationLink = this.getDocumentLink(parentDocId, selectedDocumentId)
            break

          case 'FORM_SHARE':
            notificationMessage = `The Form from template ${templateName} has been shared to you by ${userName}.`
            notificationLink = this.getFormLink(templateId, formId)
            break

          case 'TASK_UPDATE':
            notificationMessage = `The Task ${taskName} has been updated by ${userName}.`
            notificationLink = this.getTaskPlannerLink(taskId)
            break

          case 'WORKFLOWS':
            notificationMessage = `process plan initiated for ${materailProcessName} of quantity ${materailProcessQuantity} by ${userName} for ${templateName}.`
            notificationLink = this.getFormLink(templateId, this.sanitizeId(materialFormId))
            break

          case 'PROJECT_USER_INVITE':
            notificationMessage = `${userName} invited you to project ${project?.name}.`
            notificationLink = '/'
            break

          case 'USER_ROLE_UPDATED':
            if (notification.project_id) {
              const projectToken = storage.getItem(config.localstorageKeys.PROJECT)
              if (projectToken) {
                const decodedToken = jwtDecode(projectToken)
                if (decodedToken['x-hasura-project-id'] === notification.project_id && new Date(decodedToken.iat * 1000) < new Date(notification.sent_on)) {
                  alert('Your role has been updated in project! Please login again')
                  setTimeout(() => {
                    logout()
                  }, 2000)
                }
              }
            } else {
              const tenantToken = storage.getItem(config.localstorageKeys.TENANT)
              const decodedToken = jwtDecode(tenantToken)
              if (new Date(decodedToken.iat * 1000) < new Date(notification.sent_on)) {
                alert('Your role has been updated in tenant! Please login again')
                setTimeout(() => {
                  logout()
                }, 2000)
              }
            }
            continue

          case 'USER_DEACTIVATED':
            if (notification.project_id) {
              const projectToken = storage.getItem(config.localstorageKeys.PROJECT)
              if (projectToken) {
                const decodedToken = jwtDecode(projectToken)
                if (decodedToken['x-hasura-project-id'] === notification.project_id && new Date(decodedToken.iat * 1000) < new Date(notification.sent_on)) {
                  alert('You have been deactivated from this project!')
                  setTimeout(() => {
                    logout()
                  }, 2000)
                }
              }
            } else {
              const tenantToken = storage.getItem(config.localstorageKeys.TENANT)
              const decodedToken = jwtDecode(tenantToken)
              if (new Date(decodedToken.iat * 1000) < new Date(notification.sent_on)) {
                alert('You have been deactivated from this tenant!')
                setTimeout(() => {
                  logout()
                }, 2000)
              }
            }
            continue

          case 'USER_PASSWORD_UPDATED': {
            const loginToken = storage.getItem(config.localstorageKeys.AUTH)
            const decodedToken = jwtDecode(loginToken)
            if (new Date(decodedToken.iat * 1000) < new Date(notification.sent_on)) {
              alert('Session expired due to password change!')
              setTimeout(() => {
                logout()
              }, 2000)
            }
            continue

            // Add more cases for other actions as needed
          }
          default:
            notificationMessage = 'Something went wrong with this notification' // default message
          }
          if (!notificationLink && metadataRoute) {
            notificationLink = metadataRoute
          }
          if (!notificationLink && isFactory) {
            notificationLink = factoryId ? `/${factoryId}/factory-settings` : '/factories'
          }
          transformedNotifications.push({
            id,
            readStatus,
            time,
            message: notificationMessage,
            link: notificationLink,
            projectId: projectID ?? 0,
            feature,
            action,
            factoryId,
            isFactory
          })
        }
        this.allNotifications = transformedNotifications
        this.loading = false
      }
    },

    toggleHighlight (notificationId) {
      if (!this.highlightedNotificationId || this.highlightedNotificationId !== notificationId) {
        this.highlightedNotificationId = notificationId
      } else {
        this.highlightedNotificationId = null
      }
    }
  }
}
</script>

  <style scoped>
  .notification-container {
    position: relative;
  }
  .notification-box {
    background-color: #f5f5f5;
    border: 1px solid #ccc;
    border-radius: 5px;
    padding: 0px;
    position: absolute;
    top: 50px; /* Adjust the top position as needed */
    right: 10px; /* Adjust the right position as needed */
    min-width: 600px; /* Increase the minimum width */
    max-width: 600px;
    z-index: 20;
    max-height: 350px; /* Add max height to enable scrolling */
    overflow-y: auto; /* Add vertical scroll bar */
  }

  .notification-count {
    position: absolute;
    top: 4px;
    left: 20px;
    background-color: var(--brand-color);
    color: black;
    font-size: var(--text-s);
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .box {
    min-height: 10px;
  }

  .notification-navbar {
    display: flex;
    justify-content: space-between; /* Aligns children with space in between */
    align-items: center;
    font-weight: bold;
    padding: 10px 0px 10px 0px;
    background-color: var(--brand-color);
    /* border-radius: 5px 5px 0 0; */
    position: sticky;
    top: 0;
    z-index: 20;
  }

  .notification-item {
    padding: 10px;
  }

  .notification-separator {
    border-bottom: 1px solid #ccc;
    margin-top: 5px;
  }

  .notification-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  a{
    text-decoration: none !important;
    color: inherit !important;
  }

  .notification-message {
    flex-grow: 1;
    word-wrap: break-word; /* Allow long words to wrap */
    max-width: 400px;
  }

  .long-message {
    white-space: normal; /* Reset white-space to allow wrapping */
  }

  .highlighted {
  transition: background-color 0.3s; /* Add a smooth transition effect */
  color: #333;
}

/* Additional styles to improve visibility of highlighted text */
.highlighted .notification-message {
  font-weight: bold;
  /* color: #333; Change the text color as needed */
}

  .notification-time {
    font-size: var(--text-s);
    color: #888;
  }
  .unread-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: var(--brand-color);
  border-radius: 50%;
  margin-right: 4px; /* Adjust as needed */
}
  </style>
